import { useEffect, useMemo, useRef, useState } from "react";
import { IconX, IconPlus, IconChevronDown } from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";
import { IconPill } from "../../../../components/common/icon-pill";
import { Select } from "../../../../components/forms/select";
import { RequiredMark } from "../../../../components/common/required-mark";
import { useProductRams } from "../../hooks/useProductRams";
import { useProductStorageValues } from "../../hooks/useProductStorageValues";
import { useProductColors } from "../../hooks/useProductColors";
import { useProductCpus } from "../../hooks/useProductCpus";
import { VARIANT_AXES, type VariantOptionAxis } from "./variant-axis-registry";

export type { VariantOptionAxis } from "./variant-axis-registry";

export interface VariantOption {
  id: number;
  name: VariantOptionAxis;
  /** Selected attribute-value IDs for this axis, kept as strings for parity with the select <-> form layer. */
  valueIds: string[];
}

const OPTION_AXES: { value: VariantOptionAxis; label: string }[] = VARIANT_AXES.map((a) => ({
  value: a,
  label: a,
}));

export function OptionCard({
  option,
  index,
  onRemove,
  onUpdate,
  onLabelsResolved,
  readOnly = false,
}: {
  option: VariantOption;
  index: number;
  onRemove: () => void;
  onUpdate: (patch: Partial<VariantOption>) => void;
  /** Surface labels back to the parent so it can render the variants table. */
  onLabelsResolved?: (axis: VariantOptionAxis, labels: Record<string, string>) => void;
  readOnly?: boolean;
}) {
  // Pull every axis hook up-front (React hook rules). SWR dedupes across cards
  // so loading them all here costs at most one fetch per axis per page.
  const rams = useProductRams();
  const storage = useProductStorageValues();
  const colors = useProductColors();
  const cpus = useProductCpus();

  type AxisDataSource = {
    options: { value: string; label: string }[];
    isLoading: boolean;
  };

  const sourceByAxis = useMemo<Record<Exclude<VariantOptionAxis, "">, AxisDataSource>>(() => {
    const toOptions = <T extends { id: number; name: string }>(
      list: T[] | undefined,
    ): { value: string; label: string }[] =>
      (list ?? []).map((x) => ({ value: String(x.id), label: x.name }));

    return {
      "RAM": { options: toOptions(rams.data), isLoading: rams.isLoading },
      "Storage": { options: toOptions(storage.data), isLoading: storage.isLoading },
      "Color": { options: toOptions(colors.data), isLoading: colors.isLoading },
      "CPU": { options: toOptions(cpus.data), isLoading: cpus.isLoading },
    };
  }, [rams.data, rams.isLoading, storage.data, storage.isLoading, colors.data, colors.isLoading,
      cpus.data, cpus.isLoading]);

  const allOptions = option.name ? sourceByAxis[option.name].options : [];
  const isLoading = option.name ? sourceByAxis[option.name].isLoading : false;

  const labelMap = useMemo(() => {
    const m: Record<string, string> = {};
    allOptions.forEach((o) => { m[o.value] = o.label; });
    return m;
  }, [allOptions]);

  useEffect(() => {
    if (option.name && onLabelsResolved) {
      onLabelsResolved(option.name, labelMap);
    }
  }, [option.name, labelMap, onLabelsResolved]);

  const remaining = allOptions.filter((o) => !option.valueIds.includes(o.value));

  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-borderDefault bg-bgLight p-4">
      <div className="flex items-center justify-between">
        <Text variant="bodySmBold" tone="dark">Option {index + 1}</Text>
        {!readOnly && (
          <IconPill
            as="button"
            size={24}
            tone="ghost"
            aria-label="Remove option"
            onClick={onRemove}
            className="!bg-transparent !text-textMuted hover:!text-textDark"
          >
            <IconX size={16} />
          </IconPill>
        )}
      </div>
      <div className="flex items-end gap-6 max-md:flex-col max-md:items-stretch max-md:gap-4">
        <div className="w-44 shrink-0 max-md:w-full">
          <Select
            name={`option-name-${option.id}`}
            label="Option Name"
            required={!readOnly}
            density="compact"
            value={option.name}
            onChange={(e) => onUpdate({ name: e.target.value as VariantOptionAxis, valueIds: [] })}
            options={OPTION_AXES}
            placeholder="Select option"
            disabled={readOnly}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Text variant="bodySmMedium" tone="dark">
            Values:
            {!readOnly && <RequiredMark />}
          </Text>
          <div className="flex flex-wrap items-center gap-2">
            {option.valueIds.map((valId) => (
              <span key={valId} className="flex items-center gap-1 rounded-full border border-borderDefault bg-bgWhite px-3 py-1.5">
                <Text variant="bodySm" tone="dark">{labelMap[valId] ?? valId}</Text>
                {!readOnly && (
                  <button
                    onClick={() => onUpdate({ valueIds: option.valueIds.filter((v) => v !== valId) })}
                    className="ml-0.5 text-textMuted hover:text-textDark"
                  >
                    <IconX size={11} />
                  </button>
                )}
              </span>
            ))}

            {!readOnly && (
              <div ref={pickerRef} className="relative">
                <button
                  disabled={!option.name || isLoading || remaining.length === 0}
                  onClick={() => setPickerOpen((v) => !v)}
                  className="flex items-center gap-1 rounded-full border border-borderDefault bg-bgWhite px-3 py-1.5 transition-colors hover:border-borderDark disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <IconPlus size={11} className="text-textBody" />
                  <Text variant="bodySm" tone="body">
                    {isLoading
                      ? "Loading…"
                      : !option.name
                        ? "Pick option first"
                        : remaining.length === 0
                          ? "All added"
                          : "Add"}
                  </Text>
                  <IconChevronDown size={11} className="text-textBody" />
                </button>
                {pickerOpen && remaining.length > 0 && (
                  <div className="absolute left-0 top-full z-10 mt-1 w-48 rounded-xl border border-borderDefault bg-bgWhite py-1 shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)]">
                    {remaining.map((o) => (
                      <button
                        key={o.value}
                        onClick={() => {
                          onUpdate({ valueIds: [...option.valueIds, o.value] });
                          setPickerOpen(false);
                        }}
                        className="flex w-full items-center px-4 py-2 transition-colors hover:bg-bgLight"
                      >
                        <Text variant="bodySm" tone="dark">{o.label}</Text>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
